import { useLogto } from '@logto/react';
import { useEffect, useRef, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { getEmbeddedBrowserInfo } from '../auth/EmbeddedBrowser';
import { clearLogtoStorage } from '../auth/recovery';
import { useTheme } from '../contexts/ThemeContext';

const LOGIN_TIMEOUT_MS = import.meta.env.LOGIN_TIMEOUT_MS || 6000;

/**
 * Redirect target for unauthenticated users. Bounces to Logto's hosted
 * sign-in UI, or back home if already authed.
 *
 * `signIn` is called at most once per mount. `useLogto()` returns fresh
 * function references on every render, so an unguarded effect can fire
 * `signIn` again before the redirect leaves the page — that races against
 * itself, can rewrite the PKCE state mid-flight, and lands the user on a
 * /callback that can't be exchanged. The ref keeps it to one shot.
 *
 * If the redirect doesn't kick in within a few seconds (rare — happens on
 * flaky mobile networks where the bootstrap call to Logto's discovery
 * endpoint stalls), surface a "Try again" button so the user is never
 * trapped on a dead page.
 *
 * Embedded-webview guard: if we detect an in-app browser (LINE, Facebook,
 * etc.), we do NOT auto-redirect — Google's OAuth screen refuses to render
 * in webviews, so the redirect would dead-end at an "access blocked" page.
 * Instead we render an instructional notice asking the user to reopen the
 * link in their real browser, with a "continue anyway" escape hatch in case
 * detection is a false positive. Real browsers see `isEmbedded === false`
 * and the original flow is entirely unchanged.
 */
export function Login() {
  const { signIn, isAuthenticated, isLoading } = useLogto();
  const { theme } = useTheme();
  const { t } = useTranslation();
  const triggeredRef = useRef(false);
  const [showRecovery, setShowRecovery] = useState(false);
  // Computed once on mount; UA does not change within a page lifetime.
  const [embedded] = useState(getEmbeddedBrowserInfo);
  // Lets the user override a false-positive detection and proceed anyway.
  const [bypassEmbedded, setBypassEmbedded] = useState(false);
  const [copied, setCopied] = useState(false);

  const blockedByWebview = embedded.isEmbedded && !bypassEmbedded;

  useEffect(() => {
    if (isLoading) return;
    if (isAuthenticated) {
      window.location.replace('/');
      return;
    }
    if (blockedByWebview) return;
    if (triggeredRef.current) return;
    triggeredRef.current = true;
    void signIn(`${window.location.origin}/callback`);
  }, [signIn, isAuthenticated, isLoading, blockedByWebview]);

  useEffect(() => {
    if (blockedByWebview) return;
    const timer = window.setTimeout(() => setShowRecovery(true), LOGIN_TIMEOUT_MS);
    return () => window.clearTimeout(timer);
  }, [blockedByWebview]);

  const handleReset = () => {
    clearLogtoStorage();
    triggeredRef.current = false;
    window.location.replace('/login');
  };

  const handleCopyUrl = async () => {
    try {
      await navigator.clipboard.writeText(window.location.href);
      setCopied(true);
      window.setTimeout(() => setCopied(false), 2000);
    } catch {
      // Clipboard can be blocked inside some webviews; the manual
      // "open in browser" instruction still applies, so fail silently.
    }
  };

  if (blockedByWebview) {
    return (
      <div className={`auth-screen theme-${theme}`}>
        <div className="webview-notice">
          <div className="webview-notice__title">{t('webviewTitle')}</div>
          <p className="webview-notice__body">{t('webviewBody')}</p>
          <button type="button" className="webview-notice__copy" onClick={handleCopyUrl}>
            {copied ? t('webviewCopied') : t('webviewCopyUrl')}
          </button>
          <button type="button" className="link-btn" onClick={() => setBypassEmbedded(true)}>
            {t('webviewContinue')}
          </button>
        </div>
      </div>
    );
  }

  return (
    <div className={`auth-screen theme-${theme}`}>
      <div className="loading-screen">
        <div>{t('signingIn')}</div>
        {showRecovery && (
          <button type="button" className="link-btn" onClick={handleReset}>
            {t('signInRetry')}
          </button>
        )}
      </div>
    </div>
  );
}
